import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '@/services/api';
import {
  EntityTranslationsOut,
  EntityTranslationsUpdate,
  TranslatableEntityType,
  TranslationLocale,
} from '@/types/api';
import { FormField } from '../FormField';
import './TranslationFields.scss';

const LOCALES: TranslationLocale[] = ['en', 'fr', 'pt'];

type Values = Record<string, Record<TranslationLocale, string>>;

export interface TranslationFieldsProps {
  entityType: TranslatableEntityType;
  entityId: number;
  disabled?: boolean;
}

export interface TranslationFieldsHandle {
  /** Saves the edited translations (no request if nothing changed). */
  save: () => Promise<void>;
}

const toValues = (data: EntityTranslationsOut): Values =>
  Object.fromEntries(
    Object.entries(data.fields).map(([field, translation]) => [
      field,
      { en: translation.en ?? '', fr: translation.fr ?? '', pt: translation.pt ?? '' },
    ])
  );

/**
 * Collapsible "Translations" section for an existing entity's edit form.
 * Translations are machine-generated the first time the entity is saved;
 * here they can be corrected by hand. The parent form calls `save()` (via
 * ref) right after its own update succeeds. Renders nothing if the user
 * cannot edit the entity's translations.
 */
const TranslationFields = forwardRef<TranslationFieldsHandle, TranslationFieldsProps>(
  ({ entityType, entityId, disabled = false }, ref) => {
    const { t } = useTranslation();
    const [data, setData] = useState<EntityTranslationsOut | null>(null);
    const [initial, setInitial] = useState<Values>({});
    const [values, setValues] = useState<Values>({});
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [hidden, setHidden] = useState(false);

    useEffect(() => {
      let cancelled = false;
      setLoading(true);
      setLoadError(false);
      setHidden(false);
      apiService
        .getEntityTranslations(entityType, entityId)
        .then((result) => {
          if (cancelled) return;
          setData(result);
          setInitial(toValues(result));
          setValues(toValues(result));
        })
        .catch((err: any) => {
          if (cancelled) return;
          const statusCode = err?.response?.status;
          if (statusCode === 403 || statusCode === 404) {
            setHidden(true);
          } else {
            setLoadError(true);
          }
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }, [entityType, entityId]);

    useImperativeHandle(ref, () => ({
      save: async () => {
        if (!data) return;
        const payload: EntityTranslationsUpdate = { fields: {} };
        Object.entries(values).forEach(([field, byLocale]) => {
          LOCALES.forEach((locale) => {
            if (byLocale[locale] !== initial[field]?.[locale]) {
              payload.fields[field] = { ...payload.fields[field], [locale]: byLocale[locale] };
            }
          });
        });
        if (Object.keys(payload.fields).length === 0) return;
        const result = await apiService.updateEntityTranslations(entityType, entityId, payload);
        setData(result);
        setInitial(toValues(result));
        setValues(toValues(result));
      },
    }), [data, values, initial, entityType, entityId]);

    if (hidden) return null;

    const handleChange = (field: string, locale: TranslationLocale, value: string) => {
      setValues((prev) => ({ ...prev, [field]: { ...prev[field], [locale]: value } }));
    };

    // Only fields with original text have something to translate.
    const fields = data
      ? Object.entries(data.fields).filter(([, translation]) => !!translation.original)
      : [];

    return (
      <details className="translation-fields">
        <summary className="translation-fields__summary">{t('translationEditor.title')}</summary>
        <div className="translation-fields__body">
          <p className="translation-fields__hint">{t('translationEditor.hint')}</p>
          {loading && <p className="translation-fields__status">{t('translationEditor.loading')}</p>}
          {loadError && <p className="translation-fields__status">{t('translationEditor.loadError')}</p>}
          {!loading && data && fields.length === 0 && (
            <p className="translation-fields__status">{t('translationEditor.empty')}</p>
          )}
          {fields.map(([field, translation]) => (
            <div key={field} className="translation-fields__field">
              <h4 className="translation-fields__field-title">
                {t(`translationEditor.fields.${field}`, field)}
              </h4>
              <p className="translation-fields__original">
                <span>{t('translationEditor.original')}:</span> {translation.original}
              </p>
              {LOCALES.map((locale) => (
                <FormField
                  key={locale}
                  type={field === 'description' ? 'textarea' : 'text'}
                  rows={field === 'description' ? 2 : undefined}
                  name={`translation_${field}_${locale}`}
                  label={t(`languages.${locale}`)}
                  value={values[field]?.[locale] ?? ''}
                  placeholder={translation.original ?? ''}
                  onChange={(e) => handleChange(field, locale, e.target.value)}
                  disabled={disabled}
                />
              ))}
            </div>
          ))}
        </div>
      </details>
    );
  }
);

TranslationFields.displayName = 'TranslationFields';

export default TranslationFields;
